<template>
  <div class="atlas-ingestion-dropzone">
    <button type="button" class="atlas-ingestion-dropzone__hero-icon" aria-label="Choose folder to import" :disabled="isProcessingSelection" @click="emitChooseFolder">
      <IconEmbedded name="upload_2" :size="36" color="#118689" aria-hidden="true" />
    </button>
    <h3 class="atlas-ingestion-dropzone__title">Import Your Image Library</h3>
    <p class="atlas-ingestion-dropzone__description global-p">Select the folder where your photos live. We'll scan the tree and propose groups for you to review.</p>

    <div v-if="isProcessingSelection" class="atlas-ingestion-dropzone__panel atlas-ingestion-dropzone__panel--processing" @dragover="onDragOverRejectDrop" @drop.prevent>
      <div class="atlas-ingestion-dropzone__panel-inner atlas-ingestion-dropzone__panel-inner--processing" role="status" aria-live="polite">
        <LoadingIcon :size="36" class="atlas-ingestion-dropzone__processing-spinner" />
        <p class="atlas-ingestion-dropzone__panel-heading">Scanning selected folder…</p>
        <p class="atlas-ingestion-dropzone__panel-hint">This can take a minute for large libraries.</p>
      </div>
    </div>
    <div
      v-else
      class="atlas-ingestion-dropzone__panel"
      role="button"
      tabindex="0"
      @click="onPanelActivate"
      @keydown.enter.prevent="emitChooseFolder"
      @keydown.space.prevent="emitChooseFolder"
      @dragover="onDragOverRejectDrop"
      @drop.prevent
    >
      <div class="atlas-ingestion-dropzone__panel-inner">
        <IconEmbedded class="atlas-ingestion-dropzone__panel-icon" name="upload_2" :size="28" color="#118689" />
        <p class="atlas-ingestion-dropzone__panel-heading">Select a folder to import</p>
        <p class="atlas-ingestion-dropzone__panel-hint">recommend fewer than 10,000 total files<br />(subfolders are scanned automatically)</p>
        <SrpButton color="teal" fill="solid" class="atlas-ingestion-dropzone__choose-btn" @click.stop="emitChooseFolder">
          <template #icon><IconEmbedded name="upload_2" :size="17" /></template>
          Choose Folder
        </SrpButton>
        <!--<p class="atlas-ingestion-dropzone__footer-note">jpg images supported</p>-->
      </div>
    </div>

    <p class="atlas-ingestion-dropzone__support">
      <span class="atlas-ingestion-dropzone__support-label">Questions?</span>
      <CopyTextNew><b>support@shrpa.com</b></CopyTextNew>
    </p>
  </div>
</template>

<script setup lang="ts">
import SrpButton from "@components/ui/SrpButton.vue";
import IconEmbedded from "@components/ui/IconEmbedded.vue";
import LoadingIcon from "@components/ui/LoadingIcon.vue";
import CopyTextNew from "@components/CopyTextNew.vue";

defineOptions({
  name: "AtlasIngestionChooseFolderStep",
});

withDefaults(
  defineProps<{
    /** When true, the dashed panel shows a spinner instead of the choose-folder UI. */
    isProcessingSelection?: boolean;
  }>(),
  {
    isProcessingSelection: false,
  }
);

const emit = defineEmits<{
  chooseFolder: [];
}>();

function emitChooseFolder() {
  emit("chooseFolder");
}

function onPanelActivate(event: MouseEvent) {
  const target = event.target as HTMLElement | null;
  if (target?.closest(".srp-button")) {
    return;
  }
  emitChooseFolder();
}

/** Drag-and-drop is disabled; keep default navigation on accidental drops from opening the file in-tab. */
function onDragOverRejectDrop(e: DragEvent) {
  e.preventDefault();
  if (e.dataTransfer) {
    e.dataTransfer.dropEffect = "none";
  }
}
</script>

<style scoped lang="scss">
.atlas-ingestion-dropzone {
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  max-width: 560px;
  margin: 0 auto;
}

.atlas-ingestion-dropzone__hero-icon {
  width: 88px;
  height: 88px;
  margin: 0 0 20px;
  padding: 0;
  border: none;
  border-radius: 50%;
  background: rgba(17, 134, 137, 0.1);
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: background 0.15s ease;

  &:hover {
    background: rgba(17, 134, 137, 0.16);
  }

  &:focus-visible {
    outline: 2px solid #118689;
    outline-offset: 2px;
  }

  &:disabled {
    cursor: not-allowed;
    opacity: 0.55;
    pointer-events: none;
  }
}

.atlas-ingestion-dropzone__title {
  margin: 0 0 12px;
  font:
    700 22px/1.25 "Quicksand",
    sans-serif;
  color: #1a1a1a;
}

.atlas-ingestion-dropzone__description {
  margin: 0 0 28px;
  color: var(--srp-color-text-muted, #666);
  font-size: 15px;
  line-height: 1.5;
  max-width: 48ch;
}

.atlas-ingestion-dropzone__panel {
  width: 100%;
  max-width: 520px;
  border: 2px dashed #c8c8c8;
  border-radius: 12px;
  padding: 28px 24px 22px;
  cursor: pointer;
  background: #fff;
  transition:
    border-color 0.15s ease,
    background 0.15s ease;

  &:hover {
    border-color: #118689;
    background: rgba(17, 134, 137, 0.02);
  }

  &:focus-visible {
    outline: 2px solid #118689;
    outline-offset: 2px;
  }

  &--processing {
    cursor: default;
    pointer-events: none;

    &:hover {
      border-color: #c8c8c8;
      background: #fff;
    }
  }
}

.atlas-ingestion-dropzone__panel-inner {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;

  &--processing {
    gap: 10px;
    padding: 4px 0;
  }
}

.atlas-ingestion-dropzone__processing-spinner {
  flex-shrink: 0;
}

.atlas-ingestion-dropzone__panel-icon {
  margin-bottom: 4px;
}

.atlas-ingestion-dropzone__panel-heading {
  margin: 0;
  font:
    700 17px/1.3 "Quicksand",
    sans-serif;
  color: #1a1a1a;
}

.atlas-ingestion-dropzone__panel-hint {
  margin: 0 0 16px;
  font-size: 14px;
  line-height: 1.45;
  color: var(--srp-color-text-muted, #666);
  max-width: 36ch;
}

.atlas-ingestion-dropzone__choose-btn {
  margin-bottom: 4px;
}

.atlas-ingestion-dropzone__footer-note {
  margin: 12px 0 0;
  font-size: 13px;
  color: var(--srp-color-text-muted, #888);
}

.atlas-ingestion-dropzone__support {
  margin: 25px 0 0;
  width: 100%;
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: center;
  gap: 0 10px;
  font-size: 14px;
  line-height: 1.5;
  color: var(--srp-color-text-muted, #666);
}

.atlas-ingestion-dropzone__support-label {
  white-space: nowrap;
}
</style>
